/**
 * ORDENAÇÃO e FILTROS (status, período, faixa de valor) da lista de orçamentos.
 *
 * Vive fora da página pelo mesmo motivo do `quotesListSummary`: são regras que
 * mudam QUAIS documentos o usuário vê e em que ordem, e todas elas erram em
 * silêncio — uma lista filtrada errado não dá erro, só esconde trabalho.
 *
 * ─── O QUE QUEBRA SE FOR ESCRITO NA PÁGINA ──────────────────────────────────
 *
 * · **Dinheiro** (faixa de valor e ordenação por valor) passa por
 *   `quoteTotalNumber`, o MESMO parser estrito do resumo do hero. Um
 *   `Number(quote.total)` local aceitaria `'0x10'` e faria a faixa recortar
 *   documentos por um valor que ninguém digitou (#123a).
 * · **Nome de cliente** ordena por `normalizeSearchText`, o mesmo da busca:
 *   sem dobrar acento, "Ângela" vai para depois de "Zuleica" — a tabela ASCII
 *   ordenando um app pt-BR.
 * · **Ordem instável** faz linhas trocarem de lugar sozinhas entre renders
 *   quando há empate (dois documentos do mesmo dia, dois do mesmo valor). Todo
 *   comparador desempata por `id` (#93).
 * · **Período** compara `created_at`, que é TIMESTAMP: a faixa vem de
 *   `resolvePeriodPreset`, que já ancora as pontas em dia inteiro. Comparar
 *   string de data aqui reintroduziria o erro de fuso do #36/#114.
 */
import type { Quote, QuoteStatus } from '@/types/database';
import { quoteTotalNumber } from '@/lib/quotesListSummary';
import { normalizeSearchText } from '@/lib/quoteSearch';
import type { ReportDateRange, ReportPeriodPreset } from '@/lib/reportPeriod';

// ─── Ordenação ───────────────────────────────────────────────────────────────

export type QuoteSortOption = 'recent' | 'oldest' | 'highest' | 'lowest' | 'client' | 'title';

export const QUOTE_SORT_LABELS: Record<QuoteSortOption, string> = {
  recent: 'Mais recentes',
  oldest: 'Mais antigos',
  highest: 'Maior valor',
  lowest: 'Menor valor',
  client: 'Nome do cliente',
  title: 'Título do orçamento',
};

/** Ordem do menu. `recent` é o padrão — é como a lista sempre se comportou. */
export const QUOTE_SORT_ORDER: readonly QuoteSortOption[] = [
  'recent',
  'oldest',
  'highest',
  'lowest',
  'client',
  'title',
];

export const DEFAULT_QUOTE_SORT: QuoteSortOption = 'recent';

function timeOf(quote: Quote): number {
  const parsed = new Date((quote as { created_at?: string }).created_at ?? '').getTime();
  // Data ilegível vai para o FIM em "mais recentes" (e não para o topo, que é
  // onde `NaN → 0` a colocaria em "mais antigos"): documento sem data legível é
  // exceção, e exceção não pode ocupar a primeira linha da lista.
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Texto para ordenação alfabética. Vazio vai para o FIM (`￿`) — um punhado
 * de "Sem cliente" no topo empurraria para baixo justamente o que o usuário
 * procura ao ordenar por nome.
 */
function textOf(value: unknown): string {
  const normalized = normalizeSearchText(value);
  return normalized || '￿';
}

/** Não muta a entrada: a lista da tela é derivada, e ordenar no lugar embaralharia o estado. */
export function sortQuotesBy(quotes: readonly Quote[], option: QuoteSortOption): Quote[] {
  const rows = [...quotes];
  const byId = (a: Quote, b: Quote) => String(a.id).localeCompare(String(b.id));

  switch (option) {
    case 'oldest':
      return rows.sort((a, b) => timeOf(a) - timeOf(b) || byId(a, b));
    case 'highest':
      return rows.sort(
        (a, b) => quoteTotalNumber(b.total) - quoteTotalNumber(a.total) || byId(a, b),
      );
    case 'lowest':
      return rows.sort(
        (a, b) => quoteTotalNumber(a.total) - quoteTotalNumber(b.total) || byId(a, b),
      );
    case 'client':
      return rows.sort(
        (a, b) => textOf(a.client_name).localeCompare(textOf(b.client_name)) || byId(a, b),
      );
    case 'title':
      return rows.sort((a, b) => textOf(a.title).localeCompare(textOf(b.title)) || byId(a, b));
    case 'recent':
    default:
      return rows.sort((a, b) => timeOf(b) - timeOf(a) || byId(a, b));
  }
}

// ─── Filtros ─────────────────────────────────────────────────────────────────

/**
 * Presets oferecidos no modal de filtro. Subconjunto EXPLÍCITO dos de
 * `reportPeriod` (mesmo padrão do `DASHBOARD_PRESETS`) — os longos de relatório
 * não fazem sentido para achar um documento, e `custom` exigiria um calendário
 * dentro do modal.
 */
export const QUOTE_PERIOD_PRESETS: readonly ReportPeriodPreset[] = [
  'today',
  'thisWeek',
  'thisMonth',
  '30d',
  '3m',
  'thisYear',
];

export interface QuoteValueRange {
  /** `null` = sem limite. NUNCA `0`, que é um limite de verdade. */
  min: number | null;
  max: number | null;
}

export const EMPTY_VALUE_RANGE: QuoteValueRange = { min: null, max: null };

/**
 * `null` em `min`/`max` é "sem limite", e a distinção importa: `0` é um piso
 * legítimo (achar os documentos zerados), e colapsar os dois — o clássico
 * `Number('') === 0` do #35c — transformaria "sem mínimo" em "no mínimo zero",
 * que é diferente sempre que houver documento negativo.
 */
export function matchesValueRange(quote: Quote, range: QuoteValueRange): boolean {
  const total = quoteTotalNumber(quote.total);
  if (range.min !== null && total < range.min) return false;
  if (range.max !== null && total > range.max) return false;
  return true;
}

/** `range` nulo = sem filtro de período. */
export function matchesPeriod(quote: Quote, range: ReportDateRange | null): boolean {
  if (!range) return true;
  const time = new Date((quote as { created_at?: string }).created_at ?? '').getTime();
  // Data ilegível NÃO é recortada: sumir com um documento por causa de um
  // carimbo estranho é pior do que mostrá-lo fora do período pedido.
  if (!Number.isFinite(time)) return true;
  return time >= range.from.getTime() && time <= range.to.getTime();
}

/**
 * Quantos filtros do MODAL estão ativos — vira o selo no ícone.
 *
 * O status fica de FORA da contagem de propósito: ele tem os botões visíveis
 * logo acima, então já se anuncia sozinho. Contar um filtro que a tela mostra
 * faria o selo dizer "1" com o modal inteiro em branco.
 */
export function countActiveModalFilters(
  period: ReportPeriodPreset | null,
  value: QuoteValueRange,
): number {
  let active = 0;
  if (period) active += 1;
  if (value.min !== null || value.max !== null) active += 1;
  return active;
}

// ─── Contagem por status (os selos dos botões) ───────────────────────────────

export type QuoteStatusFilter = 'all' | QuoteStatus;

/**
 * Quantos documentos cada botão traria — contados sobre o recorte JÁ filtrado
 * por aba, busca, período e valor, mas ANTES do status.
 *
 * É a única definição que não mente: contar sobre a lista inteira faria o botão
 * prometer 12 e a tela entregar 3 assim que houvesse uma busca ativa; contar
 * depois do status faria todos os outros botões marcarem zero.
 */
export function countQuotesByStatus(
  quotes: readonly Quote[],
): Record<QuoteStatusFilter, number> {
  const counts: Record<QuoteStatusFilter, number> = {
    all: quotes.length,
    pending: 0,
    approved: 0,
    completed: 0,
    cancelled: 0,
    rejected: 0,
  };
  for (const quote of quotes) {
    const status = String(quote?.status) as QuoteStatus;
    if (status in counts && status !== ('all' as string)) counts[status] += 1;
  }
  return counts;
}
